import { getInitData, getTimeZone } from './telegram'

/**
 * Клиент API напоминаний.
 *
 * Единственное, ради чего приложение вообще ходит в сеть. Всё
 * остальное работает офлайн: результаты лежат в CloudStorage.
 * Поэтому каждый вызов может тихо провалиться - приложение обязано
 * продолжать работать без сервера.
 */

const BASE = import.meta.env['VITE_API_URL'] ?? ''

export interface ReminderSettings {
  readonly subscribed: boolean
  readonly enabled: boolean
  readonly hour: number
}

export const DEFAULT_HOUR = 19

const TIMEOUT_MS = 6000

async function post<T>(path: string, body: Record<string, unknown>): Promise<T | null> {
  if (!BASE) return null

  const initData = getInitData()
  // вне Telegram подписи нет, сервер всё равно откажет - не ходим зря
  if (!initData) return null

  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), TIMEOUT_MS)

  try {
    const res = await fetch(`${BASE}${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ initData, ...body }),
      signal: controller.signal,
    })
    if (!res.ok) return null
    return (await res.json()) as T
  } catch {
    // сеть недоступна или сервер молчит - не наша забота,
    // напоминания просто не настроятся
    return null
  } finally {
    clearTimeout(timer)
  }
}

export async function loadReminder(): Promise<ReminderSettings | null> {
  const data = await post<{ subscribed: boolean; enabled?: boolean; hour?: number }>(
    '/api/me',
    {},
  )
  if (!data) return null
  return {
    subscribed: data.subscribed,
    enabled: data.enabled ?? false,
    hour: data.hour ?? DEFAULT_HOUR,
  }
}

export async function saveReminder(
  hour: number,
  enabled: boolean,
  lang: string,
): Promise<boolean> {
  const { tz, offset } = getTimeZone()
  const data = await post<{ ok: boolean }>('/api/subscribe', { tz, offset, hour, enabled, lang })
  return data?.ok === true
}

/** Отметить пройденную тренировку. Дату ставит сервер по местному времени. */
export async function reportWorkoutDone(): Promise<void> {
  await post('/api/done', {})
}
